import Decimal from "decimal.js";
import { useMemo } from "react";

import { Order, Package, PackageItem } from "@megaron/fulfillment-contracts";

import { trackingStatusesBlockingSchedule } from "./constants";

export function useOrderPackingDerived(
  order: Order | undefined,
  packages: Package[],
  toPackItems: PackageItem[],
  scannedCode: string | null,
  activeId: number | null,
  getWeight: (pkg: Package) => number,
) {
  const allPackagesHaveTracking = useMemo(
    () =>
      packages.length > 0 &&
      packages.every((p) => typeof p.trackingNumber === "string" && p.trackingNumber.trim() !== ""),
    [packages],
  );
  const allItemsPacked = useMemo(() => toPackItems.every((i) => i.quantity === 0), [toPackItems]);
  const shouldShowCourierButton = useMemo(() => {
    if (!order) return false;
    if (order.packages.length === 0) return false;
    const allPacked = order.packages.every(
      (p) => p.shipmentId && (p.trackingStatus === "confirmed" || p.trackingNumber),
    );
    const hasInPostDelivery =
      order.deliveryMethod === "inPostCourier" || order.deliveryMethod === "inPostLocker";
    return allPacked && hasInPostDelivery;
  }, [order, packages]);
  const isCourierAlreadyScheduled = useMemo(() => {
    if (!order) return false;
    const source = order.packages.length ? order.packages : packages;
    const hasDispatchOrder = source.some((p) => p.dispatchOrderId !== null);
    const hasBlockingTrackingStatus = source.some(
      (p) =>
        p.trackingStatus != null && trackingStatusesBlockingSchedule.includes(p.trackingStatus),
    );
    return hasDispatchOrder || hasBlockingTrackingStatus;
  }, [order, packages]);
  const projectedWeightForScan = useMemo(() => {
    if (!scannedCode || activeId === null) return null;
    const item = toPackItems.find((i) => i.barcode === scannedCode);
    if (!item) return null;
    const current = packages[activeId - 1];
    return Number(new Decimal(getWeight(current)).plus(Number(item.weight ?? 0)));
  }, [scannedCode, activeId, toPackItems, packages, getWeight]);
  return {
    allPackagesHaveTracking,
    allItemsPacked,
    shouldShowCourierButton,
    isCourierAlreadyScheduled,
    projectedWeightForScan,
  };
}
